import { DatePipe, registerLocaleData, TitleCasePipe } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
	HttpClientModule,
	HTTP_INTERCEPTORS,
	HttpClient,
} from "@angular/common/http";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from "./app-routing.module";

import { StyleClassModule } from "primeng/styleclass";
import { AccordionModule } from "primeng/accordion";
import { AutoCompleteModule } from "primeng/autocomplete";
import { AvatarModule } from "primeng/avatar";
import { AvatarGroupModule } from "primeng/avatargroup";
import { BadgeModule } from "primeng/badge";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { CardModule } from "primeng/card";
import { CarouselModule } from "primeng/carousel";
import { CascadeSelectModule } from "primeng/cascadeselect";
import { ChartModule } from "primeng/chart";
import { CheckboxModule } from "primeng/checkbox";
import { ChipModule } from "primeng/chip";
import { ChipsModule } from "primeng/chips";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ConfirmPopupModule } from "primeng/confirmpopup";
import { ColorPickerModule } from "primeng/colorpicker";
import { ContextMenuModule } from "primeng/contextmenu";
import { DataViewModule } from "primeng/dataview";
import { DialogModule } from "primeng/dialog";
import { DividerModule } from "primeng/divider";
import { DropdownModule } from "primeng/dropdown";
import { FullCalendarModule } from "@fullcalendar/angular";
import { FieldsetModule } from "primeng/fieldset";
import { FileUploadModule } from "primeng/fileupload";
import { GalleriaModule } from "primeng/galleria";
import { ImageModule } from "primeng/image";
import { InplaceModule } from "primeng/inplace";
import { InputNumberModule } from "primeng/inputnumber";
import { InputMaskModule } from "primeng/inputmask";
import { InputSwitchModule } from "primeng/inputswitch";
import { InputTextModule } from "primeng/inputtext";
import { InputTextareaModule } from "primeng/inputtextarea";
import { KnobModule } from "primeng/knob";
import { ListboxModule } from "primeng/listbox";
import { MegaMenuModule } from "primeng/megamenu";
import { MenuModule } from "primeng/menu";
import { MenubarModule } from "primeng/menubar";
import { MessagesModule } from "primeng/messages";
import { MessageModule } from "primeng/message";
import { MultiSelectModule } from "primeng/multiselect";
import { OrderListModule } from "primeng/orderlist";
import { OrganizationChartModule } from "primeng/organizationchart";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { PaginatorModule } from "primeng/paginator";
import { PanelModule } from "primeng/panel";
import { PanelMenuModule } from "primeng/panelmenu";
import { PasswordModule } from "primeng/password";
import { PickListModule } from "primeng/picklist";
import { ProgressBarModule } from "primeng/progressbar";
import { RadioButtonModule } from "primeng/radiobutton";
import { RatingModule } from "primeng/rating";
import { RippleModule } from "primeng/ripple";
import { ScrollPanelModule } from "primeng/scrollpanel";
import { ScrollTopModule } from "primeng/scrolltop";
import { SelectButtonModule } from "primeng/selectbutton";
import { SidebarModule } from "primeng/sidebar";
import { SkeletonModule } from "primeng/skeleton";
import { SlideMenuModule } from "primeng/slidemenu";
import { SliderModule } from "primeng/slider";
import { SplitButtonModule } from "primeng/splitbutton";
import { SplitterModule } from "primeng/splitter";
import { StepsModule } from "primeng/steps";
import { TabMenuModule } from "primeng/tabmenu";
import { TableModule } from "primeng/table";
import { TabViewModule } from "primeng/tabview";
import { TagModule } from "primeng/tag";
import { TerminalModule } from "primeng/terminal";
import { TieredMenuModule } from "primeng/tieredmenu";
import { TimelineModule } from "primeng/timeline";
import { ToastModule } from "primeng/toast";
import { ToggleButtonModule } from "primeng/togglebutton";
import { ToolbarModule } from "primeng/toolbar";
import { TooltipModule } from "primeng/tooltip";
import { TreeModule } from "primeng/tree";
import { TreeSelectModule } from "primeng/treeselect";
import { TreeTableModule } from "primeng/treetable";
import { VirtualScrollerModule } from "primeng/virtualscroller";
import { BlockUIModule } from "primeng/blockui";
import { ScrollerModule } from "primeng/scroller";
import { ProgressSpinnerModule } from "primeng/progressspinner";

import { AppComponent } from "./app.component";
import { AppMainComponent } from "./app.main.component";
import { AppMenuComponent } from "./shared/component/menu/app.menu.component";

import { MenuService } from "./service/app.menu.service";
import { ConfigService } from "./service/app.config.service";
import { LoginComponent } from "./screens/login/login.component";
import { ConfirmationService, MessageService } from "primeng/api";
import { AppMenuitemComponent } from "./shared/component/menu/app.menuitem.component";
import { AppTopBarComponent } from "./shared/component/topbar/app.topbar.component";
import { InputNameDirective } from "./shared/directive/input-name.directive";
import { AuthInterceptorService } from "./core/services/auth-interceptor.service";
import { provideEnvironmentNgxMask } from "ngx-mask";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireMessagingModule } from "@angular/fire/compat/messaging";
import { environment } from "src/environments/environment";
import { initializeApp } from "firebase/app";
import { MyDataComponent } from "./screens/my-data/my-data.component";
import { DialogService } from "primeng/dynamicdialog";
import { DecimalTenPipe } from "./decimal-ten.pipe";
import { Select2Module } from "ng-select2-component";
import { SelectsTranslationPipe } from "./selects-translation.pipe";
import { UsersComponent } from "./screens/users/users.component";
import { InvestmentsComponent } from "./screens/investments/investments.component";
import { NotificationsComponent } from "./screens/notifications/notifications.component";
import { SendNotificationsComponent } from "./screens/send-notifications/send-notifications.component";
import { ImageCropperComponent } from "ngx-image-cropper";
import localeEs from '@angular/common/locales/es';
import { DashboardComponent } from "./screens/dashboard/dashboard.component";
import { TransactionsComponent } from "./screens/transactions/transactions.component";
import { InvestmentsUserComponent } from "./screens/investments-user/investments-user.component";
import { ImportComponent } from "./screens/import/import.component";
import { GraphicsComponent } from "./screens/graphics/graphics.component";
initializeApp(environment.firebaseConfig);

registerLocaleData(localeEs, 'es-ES');
const screens = [MyDataComponent, UsersComponent, InvestmentsComponent, InvestmentsUserComponent, TransactionsComponent];

const shared = [
	AppTopBarComponent,
	AppMenuComponent,
	AppMenuitemComponent,

	// DIRECTIVES
	InputNameDirective,
];

@NgModule({
	imports: [
		AngularFireModule.initializeApp(environment.firebaseConfig),
		AngularFireMessagingModule,
		Select2Module,
		BrowserModule,
		ImageCropperComponent,
		AppRoutingModule,
		FormsModule,
		ReactiveFormsModule,
		HttpClientModule,
		BrowserAnimationsModule,
		AccordionModule,
		AutoCompleteModule,
		AvatarModule,
		AvatarGroupModule,
		BadgeModule,
		BreadcrumbModule,
		ButtonModule,
		CalendarModule,
		CardModule,
		CarouselModule,
		CascadeSelectModule,
		ChartModule,
		CheckboxModule,
		ChipsModule,
		ChipModule,
		ConfirmDialogModule,
		ConfirmPopupModule,
		ColorPickerModule,
		ContextMenuModule,
		DataViewModule,
		DialogModule,
		DividerModule,
		DropdownModule,
		FieldsetModule,
		FileUploadModule,
		GalleriaModule,
		ImageModule,
		InplaceModule,
		InputNumberModule,
		InputMaskModule,
		InputSwitchModule,
		InputTextModule,
		InputTextareaModule,
		KnobModule,
		ListboxModule,
		MegaMenuModule,
		MenuModule,
		MenubarModule,
		MessageModule,
		MessagesModule,
		MultiSelectModule,
		OrderListModule,
		OrganizationChartModule,
		OverlayPanelModule,
		PaginatorModule,
		PanelModule,
		PanelMenuModule,
		PasswordModule,
		PickListModule,
		ProgressBarModule,
		RadioButtonModule,
		RatingModule,
		RippleModule,
		ScrollPanelModule,
		ScrollTopModule,
		SelectButtonModule,
		SidebarModule,
		SkeletonModule,
		SlideMenuModule,
		SliderModule,
		SplitButtonModule,
		SplitterModule,
		StepsModule,
		TagModule,
		TableModule,
		TabMenuModule,
		TabViewModule,
		TerminalModule,
		TieredMenuModule,
		TimelineModule,
		ToastModule,
		ToggleButtonModule,
		ToolbarModule,
		TooltipModule,
		TreeModule,
		TreeSelectModule,
		TreeTableModule,
		VirtualScrollerModule,
		StyleClassModule,
		BlockUIModule,
		ScrollerModule,
		ProgressSpinnerModule,
		FullCalendarModule
	],
	declarations: [
		AppComponent,
		AppMainComponent,
		DashboardComponent,
		ImportComponent,
		GraphicsComponent,
		LoginComponent,
		...screens,
		...shared,
		MyDataComponent,
		DecimalTenPipe,
		SelectsTranslationPipe,
		UsersComponent,
		InvestmentsComponent,
		InvestmentsUserComponent,
		TransactionsComponent,
		NotificationsComponent,
		SendNotificationsComponent
	],
	providers: [
		MenuService,
		ConfigService,
		MessageService,
		TitleCasePipe,
		ConfirmationService,
		DialogService,
		DatePipe,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptorService,
			multi: true,
		},
		{ provide: LOCALE_ID, useValue: "es-ES" },
		provideEnvironmentNgxMask()
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
